import React from 'react'
import { LayoutBand, colors, Separator, usePageWidth, Subheading, H3, FlowGrid, H2 } from '@fs/zion-ui'
import { Trans, useTranslation } from 'react-i18next'

import Country from './Country'
import { useSurname } from '../../../hooks/use-surname'

export default function SurnameCountriesSection({
  surname,
  surnameDisplay,
  countryColor = 'gray',
  backgroundColor = colors.gray00,
}) {
  const { t } = useTranslation()
  const atWidth = usePageWidth()
  const { data, isLoading } = useSurname(surname)

  if (isLoading) {
    return <LayoutBand>Loading...</LayoutBand>
  }

  const { countries } = data || {}

  if (!countries?.length) {
    return null
  }

  const trimmedCountries = countries.slice(0, 3)
  const numberOfCountries = trimmedCountries?.length

  return (
    <LayoutBand marginY="xl" color={backgroundColor}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <H2 size="H3" supplementary centered balanced>
          {t(
            'apac.section-templates.surname-countries-section.title',
            '{surname} is Found in These Countries in the FamilySearch Database',
            {
              surname: surnameDisplay || surname,
            }
          )}
        </H2>
      </div>
      <Separator size="lg" />
      <FlowGrid columnCount={atWidth({ default: 1, lg: numberOfCountries || 1 })} guttersY="nano">
        {trimmedCountries.map((country, index) => (
          <CountryDetails
            key={country.code}
            code={country.code}
            name={country.name}
            count={country.count}
            color={countryColor}
            index={index}
          />
        ))}
      </FlowGrid>
    </LayoutBand>
  )
}

const colorIndexMap = ['60', '40', '20']

function CountryDetails({ code, name, count, color, index }) {
  const atWidth = usePageWidth()
  const countryColors = colorIndexMap.map((suffix) => colors[`${color}${suffix}`] || colors.gray20)
  return (
    <div data-testid="surname-countries-country">
      <Separator size="md" />
      <Country
        countryCode={code}
        countryColor={countryColors[index]}
        height={atWidth({ default: '250px', lg: '212px', xl: '250px' })}
        width={atWidth({ default: '250px', lg: '210px', xl: '250px' })}
      />
      <Separator size="md" />
      <H3 centered size="H5" supplementary>
        {name}
        <Subheading>
          <Trans
            components={[]}
            defaults="{number, plural, one {{number} person in Family Tree} other {{number} people in Family Tree}}"
            i18nKey="apac.surname-countries-section.number-in-tree.caption"
            values={{
              number: count.toLocaleString(),
            }}
          />
        </Subheading>
      </H3>
      <Separator size="xl" />
    </div>
  )
}
